// src/pages/admin/Admin.jsx
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  CalendarIcon,
  ClockIcon,
  SearchIcon,
  EyeIcon,
  PencilIcon,
  RefreshIcon,
  CloudDownloadIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/outline";
import { useToast } from "../../hooks/useToast";
import { useFetchUser } from "../../hooks/user/useFetchUser";
import { useFetchScheduledEvents } from "../../hooks/events/useFetchScheduledEvents";
import { useFetchUFCSchedule } from "../../hooks/events/useFetchUFCSchedule";
import { useSyncUFCEventDetails } from "../../hooks/events/useSyncUFCEventDetails";
import { useGetActiveSubmissions } from "../../hooks/events/useGetActiveSubmissions";
import { useMakeAllInactive } from "../../hooks/events/useMakeAllInactive";
import { useRecalculateLeagueMemberScores } from "../../hooks/league/useRecalculateLeagueMemberScores";
import { setEventId, setEventName } from "../../features/event/eventReducer";
import Loading from "../../components/common/Loading";
import UserManagement from "../../components/admin/UserManagement";

const Admin = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toast = useToast();

  const {
    data: user,
    isLoading: userLoading,
    error: userError,
  } = useFetchUser();

  const {
    data: events,
    isLoading: eventsLoading,
    error: eventsError,
  } = useFetchScheduledEvents();

  // NEW: admin mutations
  const fetchUFCSchedule = useFetchUFCSchedule();
  const syncUFCEventDetails = useSyncUFCEventDetails();
  const {
    data: activeSubmissions,
    isLoading: submissionsLoading,
    refetch: refetchSubmissions,
  } = useGetActiveSubmissions();
  const makeAllInactive = useMakeAllInactive();
  const recalculateLeagueMemberScores = useRecalculateLeagueMemberScores();

  const [activeTab, setActiveTab] = useState("events");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [year, setYear] = useState(() => new Date().getFullYear().toString());
  const [dateFilter, setDateFilter] = useState(() => {
    // Default to current date in YYYY-MM-DD format
    const today = new Date();
    return today.toISOString().split("T")[0];
  });
  const [dateFilterEnabled, setDateFilterEnabled] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const isLoading = userLoading || eventsLoading;

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (events || []).filter((evt) => {
      const matchText =
        !needle ||
        evt?.Name?.toLowerCase().includes(needle) ||
        evt?.ShortName?.toLowerCase().includes(needle);
      const matchStatus =
        status === "all" || (evt?.Status || "").toLowerCase() === status;

      // Date filter logic - use the Day field which is already normalized to midnight
      let matchDate = true;
      if (dateFilterEnabled && dateFilter) {
        // evt.Day is already in format "2024-01-20T00:00:00"
        // dateFilter is in format "2024-01-20"
        const eventDay = evt.Day ? evt.Day.split("T")[0] : null;
        matchDate = eventDay === dateFilter;
      }

      return matchText && matchStatus && matchDate;
    }).sort((a, b) => new Date(b.DateTime) - new Date(a.DateTime));
  }, [events, q, status, dateFilter, dateFilterEnabled]);

  // Pagination calculations
  const totalItems = filtered.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedEvents = filtered.slice(startIndex, endIndex);

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [q, status, dateFilter, dateFilterEnabled]);

  // Helper functions for navigation with state setting
  const handleViewEvent = (eventId, eventName) => {
    dispatch(setEventId(eventId));
    dispatch(setEventName(eventName));
    navigate(`/events/${eventId}`);
  };

  const handleEditEvent = (eventId, eventName) => {
    dispatch(setEventId(eventId));
    dispatch(setEventName(eventName));
    navigate(`/admin/events/${eventId}/edit`);
  };

  const onFetchSchedule = () => {
    const numYear = parseInt(year, 10);
    if (Number.isNaN(numYear) || numYear < 2010 || numYear > 2100) {
      toast.info("Please enter a valid year (2010–2100).");
      return;
    }
    fetchUFCSchedule.mutate(numYear, {
      onSuccess: () => toast.success(`UFC ${numYear} schedule fetched & saved`),
      onError: (e) =>
        toast.error(`Failed to fetch schedule: ${e?.message ?? ""}`),
    });
  };

  const onSyncDetails = () => {
    syncUFCEventDetails.mutate(
      {},
      {
        onSuccess: () => toast.success("Event details synced successfully"),
        onError: (e) =>
          toast.error(`Failed to sync details: ${e?.message ?? ""}`),
      },
    );
  };

  const onGetActiveSubmissions = () => {
    refetchSubmissions();
  };

  const onMakeAllInactive = () => {
    makeAllInactive.mutate(
      {},
      {
        onSuccess: (data) => {
          toast.success(
            `${data.message} (${data.updatedCount} submissions updated)`,
          );
          refetchSubmissions();
        },
        onError: (e) =>
          toast.error(
            `Failed to make submissions inactive: ${e?.message ?? ""}`,
          ),
      },
    );
  };

  const onRecalculateLeagueScores = () => {
    recalculateLeagueMemberScores.mutate(undefined, {
      onSuccess: (data) => {
        toast.success(
          data.message || "League scores recalculated successfully!",
        );
      },
      onError: (e) => {
        toast.error(`Failed to recalculate scores: ${e?.message ?? ""}`);
      },
    });
  };

  if (isLoading) return <Loading />;

  if (userError) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="card p-6 max-w-md text-center">
          <h3 className="text-lg font-bold text-dark-grey mb-2">
            Error loading user
          </h3>
          <p className="text-medium-grey text-sm">{userError.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="icon-tile">
              <CalendarIcon className="w-6 h-6" aria-hidden="true" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-dark-grey">
                Admin
              </h1>
              <p className="text-medium-grey text-sm">Manage events and more</p>
            </div>
          </div>

          {/* Search + Status filter + Date filter */}
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <div className="relative flex-1 sm:w-72">
              <SearchIcon
                className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                aria-hidden="true"
              />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search events..."
                className="input pl-10 w-full"
                aria-label="Search events"
              />
            </div>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="input w-full sm:w-36"
              aria-label="Filter by status"
            >
              <option value="all">All statuses</option>
              <option value="in progress">In progress</option>
              <option value="ready">Ready</option>
              <option value="scheduled">Scheduled</option>
              <option value="final">Final</option>
            </select>

            {/* Date filter */}
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-md px-3 py-2">
              <input
                type="checkbox"
                id="dateFilterToggle"
                checked={dateFilterEnabled}
                onChange={(e) => setDateFilterEnabled(e.target.checked)}
                className="w-4 h-4 text-deep-red bg-gray-100 border-gray-300 rounded focus:ring-deep-red focus:ring-2"
              />
              <label
                htmlFor="dateFilterToggle"
                className="text-sm text-medium-grey whitespace-nowrap"
              >
                Date:
              </label>
              <input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                min="2024-01-01"
                max="2030-12-31"
                disabled={!dateFilterEnabled}
                className={`text-sm border-0 bg-transparent focus:ring-0 focus:outline-none ${
                  dateFilterEnabled ? "text-dark-grey" : "text-gray-400"
                }`}
                aria-label="Filter by date"
              />
            </div>
          </div>
        </div>
      </div>

      {/* NEW: Admin Tools (only for admins) */}
      {user?.role === "admin" && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <h2 className="text-lg md:text-xl font-bold text-dark-grey">
                Admin Tools
              </h2>
              <p className="text-medium-grey text-sm">
                UFC data ingestion controls
              </p>
            </div>

            <div className="flex flex-col gap-3 w-full md:w-auto">
              {/* Year input */}
              <div className="flex items-center gap-2">
                <label
                  htmlFor="year"
                  className="text-sm text-medium-grey whitespace-nowrap"
                >
                  Year
                </label>
                <input
                  id="year"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  className="input w-28"
                  placeholder={`${new Date().getFullYear()}`}
                />
              </div>

              {/* Action buttons */}
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={onFetchSchedule}
                  disabled={fetchUFCSchedule.isLoading}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md text-white bg-deep-red hover:bg-bright-red disabled:opacity-60 text-sm"
                >
                  <CloudDownloadIcon className="w-4 h-4" aria-hidden="true" />
                  <span className="hidden sm:inline">
                    {fetchUFCSchedule.isLoading
                      ? "Fetching..."
                      : "Fetch UFC Schedule"}
                  </span>
                  <span className="sm:hidden">
                    {fetchUFCSchedule.isLoading
                      ? "Fetching..."
                      : "Fetch Schedule"}
                  </span>
                </button>

                <button
                  onClick={onSyncDetails}
                  disabled={syncUFCEventDetails.isLoading}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md text-white bg-dark-grey hover:bg-medium-grey disabled:opacity-60 text-sm"
                >
                  <RefreshIcon className="w-4 h-4" aria-hidden="true" />
                  <span className="hidden sm:inline">
                    {syncUFCEventDetails.isLoading
                      ? "Syncing..."
                      : "Sync Event Details"}
                  </span>
                  <span className="sm:hidden">
                    {syncUFCEventDetails.isLoading
                      ? "Syncing..."
                      : "Sync Details"}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
        <div className="border-b border-gray-100 px-4 sm:px-6">
          <nav className="flex gap-4" role="tablist" aria-label="Admin tabs">
            <button
              role="tab"
              aria-selected={activeTab === "events"}
              onClick={() => setActiveTab("events")}
              className={`py-3 px-1 border-b-2 -mb-px text-sm font-bold transition-colors ${
                activeTab === "events"
                  ? "border-deep-red text-deep-red"
                  : "border-transparent text-medium-grey hover:text-dark-grey"
              }`}
            >
              Events
            </button>
            <button
              role="tab"
              aria-selected={activeTab === "submissions"}
              onClick={() => setActiveTab("submissions")}
              className={`py-3 px-1 border-b-2 -mb-px text-sm font-bold transition-colors ${
                activeTab === "submissions"
                  ? "border-deep-red text-deep-red"
                  : "border-transparent text-medium-grey hover:text-dark-grey"
              }`}
            >
              Submissions
            </button>
            {user?.role === "admin" && (
              <>
                <button
                  role="tab"
                  aria-selected={activeTab === "leagues"}
                  onClick={() => setActiveTab("leagues")}
                  className={`py-3 px-1 border-b-2 -mb-px text-sm font-bold transition-colors ${
                    activeTab === "leagues"
                      ? "border-deep-red text-deep-red"
                      : "border-transparent text-medium-grey hover:text-dark-grey"
                  }`}
                >
                  Leagues
                </button>
                <button
                  role="tab"
                  aria-selected={activeTab === "users"}
                  onClick={() => setActiveTab("users")}
                  className={`py-3 px-1 border-b-2 -mb-px text-sm font-bold transition-colors ${
                    activeTab === "users"
                      ? "border-deep-red text-deep-red"
                      : "border-transparent text-medium-grey hover:text-dark-grey"
                  }`}
                >
                  Users
                </button>
              </>
            )}
          </nav>
        </div>

        {/* Tab panel: Events */}
        {activeTab === "events" && (
          <div role="tabpanel" className="p-4 md:p-6">
            {eventsError ? (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-800">
                Failed to load events: {eventsError.message}
              </div>
            ) : totalItems === 0 ? (
              <div className="bg-gray-50 rounded-xl p-8 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CalendarIcon
                    className="w-8 h-8 text-gray-400"
                    aria-hidden="true"
                  />
                </div>
                <h3 className="text-lg font-bold text-dark-grey mb-2">
                  No events found
                </h3>
                <p className="text-medium-grey">
                  Try adjusting your search or filters.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Results info and items per page selector */}
                <div className="flex flex-col xs:flex-row xs:items-center xs:justify-between gap-3">
                  <div className="text-sm text-medium-grey">
                    <span className="hidden xs:inline">
                      Showing {startIndex + 1} to{" "}
                      {Math.min(endIndex, totalItems)} of {totalItems} events
                    </span>
                    <span className="xs:hidden">{totalItems} events</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <label
                      htmlFor="itemsPerPage"
                      className="text-sm text-medium-grey whitespace-nowrap"
                    >
                      Show:
                    </label>
                    <select
                      id="itemsPerPage"
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="input w-16"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                </div>

                {/* Desktop Table View */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-medium-grey border-b border-gray-100">
                        <th className="py-3 pr-4 font-medium">Date</th>
                        <th className="py-3 pr-4 font-medium">Event</th>
                        <th className="py-3 pr-4 font-medium">Status</th>
                        <th className="py-3 pr-4 font-medium">Fights</th>
                        <th className="py-3 pr-4 font-medium">Leagues</th>
                        <th className="py-3 pr-4 font-medium">
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedEvents.map((evt) => {
                        const dt = new Date(evt.DateTime);
                        const fights = evt.Fights?.length || 0;
                        const leaguesCount =
                          (evt.userLeagues?.length || 0) +
                          (evt.hasGlobal ? 1 : 1);
                        const statusTone =
                          (evt.Status || "").toLowerCase() === "ready"
                            ? "chip chip--ok"
                            : (evt.Status || "").toLowerCase() === "completed"
                              ? "chip chip--muted"
                              : "chip chip--info";

                        return (
                          <tr
                            key={evt.EventId}
                            className="border-b border-gray-50 hover:bg-gray-50/60"
                          >
                            <td className="py-3 pr-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <CalendarIcon
                                  className="w-4 h-4 text-deep-red"
                                  aria-hidden="true"
                                />
                                <span className="font-medium text-dark-grey">
                                  {dt.toLocaleDateString([], {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  })}
                                </span>
                                <span className="flex items-center gap-1 text-medium-grey">
                                  <ClockIcon
                                    className="w-4 h-4"
                                    aria-hidden="true"
                                  />
                                  {dt.toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </span>
                              </div>
                            </td>
                            <td className="py-3 pr-4">
                              <div className="max-w-[28ch]">
                                <div className="font-bold text-dark-grey leading-tight">
                                  {evt.Name}
                                </div>
                                <div className="text-xs text-medium-grey">
                                  {evt.ShortName}
                                </div>
                              </div>
                            </td>
                            <td className="py-3 pr-4">
                              <span className={statusTone}>
                                {evt.Status || "—"}
                              </span>
                            </td>
                            <td className="py-3 pr-4">{fights}</td>
                            <td className="py-3 pr-4">
                              <span className="chip chip--muted">
                                {leaguesCount}
                              </span>
                            </td>
                            <td className="py-3 pr-4">
                              <div className="flex gap-2">
                                <button
                                  onClick={() =>
                                    handleViewEvent(evt.EventId, evt.Name)
                                  }
                                  className="btn btn--ghost"
                                >
                                  <EyeIcon
                                    className="w-4 h-4"
                                    aria-hidden="true"
                                  />
                                  View
                                </button>
                                <button
                                  onClick={() =>
                                    handleEditEvent(evt.EventId, evt.Name)
                                  }
                                  className="btn btn--secondary"
                                >
                                  <PencilIcon
                                    className="w-4 h-4"
                                    aria-hidden="true"
                                  />
                                  Edit
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="lg:hidden space-y-3">
                  {paginatedEvents.map((evt) => {
                    const dt = new Date(evt.DateTime);
                    const fights = evt.Fights?.length || 0;
                    const leaguesCount =
                      (evt.userLeagues?.length || 0) + (evt.hasGlobal ? 1 : 1);
                    const statusTone =
                      (evt.Status || "").toLowerCase() === "ready"
                        ? "chip chip--ok"
                        : (evt.Status || "").toLowerCase() === "completed"
                          ? "chip chip--muted"
                          : "chip chip--info";

                    return (
                      <div
                        key={evt.EventId}
                        className="bg-white border border-gray-100 rounded-lg p-4 hover:shadow-sm transition-shadow"
                      >
                        {/* Event Header */}
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-bold text-dark-grey text-sm leading-tight mb-1 truncate">
                              {evt.Name}
                            </h3>
                            <p className="text-xs text-medium-grey truncate">
                              {evt.ShortName}
                            </p>
                          </div>
                          <span className={`${statusTone} ml-2 flex-shrink-0`}>
                            {evt.Status || "—"}
                          </span>
                        </div>

                        {/* Event Details */}
                        <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                          <div className="flex items-center gap-2">
                            <CalendarIcon
                              className="w-4 h-4 text-deep-red flex-shrink-0"
                              aria-hidden="true"
                            />
                            <div className="min-w-0">
                              <div className="font-medium text-dark-grey text-xs">
                                {dt.toLocaleDateString([], {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })}
                              </div>
                              <div className="text-xs text-medium-grey">
                                {dt.toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center justify-between">
                            <div className="text-xs text-medium-grey">
                              <div>{fights} fights</div>
                              <div>{leaguesCount} leagues</div>
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 pt-3 border-t border-gray-100">
                          <button
                            onClick={() =>
                              handleViewEvent(evt.EventId, evt.Name)
                            }
                            className="flex-1 btn btn--ghost text-xs"
                          >
                            <EyeIcon className="w-4 h-4" aria-hidden="true" />
                            View
                          </button>
                          <button
                            onClick={() =>
                              handleEditEvent(evt.EventId, evt.Name)
                            }
                            className="flex-1 btn btn--secondary text-xs"
                          >
                            <PencilIcon
                              className="w-4 h-4"
                              aria-hidden="true"
                            />
                            Edit
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex flex-col gap-3 pt-4 border-t border-gray-100">
                    <div className="text-sm text-medium-grey text-center sm:text-left">
                      Page {currentPage} of {totalPages}
                    </div>

                    <div className="flex items-center justify-center sm:justify-end gap-2">
                      {/* Previous button */}
                      <button
                        onClick={() =>
                          setCurrentPage((prev) => Math.max(prev - 1, 1))
                        }
                        disabled={currentPage === 1}
                        className="btn btn--ghost text-sm"
                      >
                        <ChevronLeftIcon
                          className="w-4 h-4"
                          aria-hidden="true"
                        />
                        <span className="hidden xs:inline">Previous</span>
                      </button>

                      {/* Page numbers - simplified for mobile */}
                      <div className="flex items-center gap-1">
                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                          .filter((page) => {
                            // On mobile, show fewer pages
                            const isMobile = window.innerWidth < 640;
                            if (isMobile) {
                              return (
                                Math.abs(page - currentPage) <= 1 ||
                                page === 1 ||
                                page === totalPages
                              );
                            }
                            // Desktop: show more pages
                            return (
                              page === 1 ||
                              page === totalPages ||
                              Math.abs(page - currentPage) <= 1
                            );
                          })
                          .map((page, index, array) => {
                            // Add ellipsis if there's a gap
                            const prevPage = array[index - 1];
                            const showEllipsis =
                              prevPage && page - prevPage > 1;

                            return (
                              <React.Fragment key={page}>
                                {showEllipsis && (
                                  <span className="px-1 py-1 text-medium-grey text-xs">
                                    ...
                                  </span>
                                )}
                                <button
                                  onClick={() => setCurrentPage(page)}
                                  className={`px-2 xs:px-3 py-2 text-xs xs:text-sm rounded-md border min-w-[32px] ${
                                    currentPage === page
                                      ? "border-deep-red bg-deep-red text-white"
                                      : "border-gray-200 bg-white hover:bg-gray-50"
                                  }`}
                                >
                                  {page}
                                </button>
                              </React.Fragment>
                            );
                          })}
                      </div>

                      {/* Next button */}
                      <button
                        onClick={() =>
                          setCurrentPage((prev) =>
                            Math.min(prev + 1, totalPages),
                          )
                        }
                        disabled={currentPage === totalPages}
                        className="btn btn--ghost text-sm"
                      >
                        <span className="hidden xs:inline">Next</span>
                        <ChevronRightIcon
                          className="w-4 h-4"
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Tab panel: Submissions */}
        {activeTab === "submissions" && (
          <div role="tabpanel" className="p-4 md:p-6">
            <div className="space-y-4">
              {/* Submissions Actions */}
              <div className="flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center">
                <div>
                  <h3 className="text-lg font-bold text-dark-grey">
                    Active Submissions
                  </h3>
                  <p className="text-sm text-medium-grey">
                    View and manage active event submissions
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                  <button
                    onClick={onGetActiveSubmissions}
                    disabled={submissionsLoading}
                    className="btn btn--secondary text-sm"
                  >
                    <RefreshIcon className="w-4 h-4" aria-hidden="true" />
                    {submissionsLoading
                      ? "Loading..."
                      : "Get Active Submissions"}
                  </button>
                  <button
                    onClick={onMakeAllInactive}
                    disabled={makeAllInactive.isLoading}
                    className="btn btn--primary text-sm"
                  >
                    {makeAllInactive.isLoading
                      ? "Processing..."
                      : "Make All Inactive"}
                  </button>
                </div>
              </div>

              {/* Submissions Data Display */}
              {activeSubmissions && (
                <div className="space-y-4">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-gradient-to-br from-deep-red to-bright-red rounded-xl p-4 text-white">
                      <div className="text-sm opacity-90 mb-1">
                        Total Active Submissions
                      </div>
                      <div className="text-3xl font-bold">
                        {activeSubmissions.totalActiveSubmissions || 0}
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-dark-grey to-medium-grey rounded-xl p-4 text-white">
                      <div className="text-sm opacity-90 mb-1">
                        Events with Submissions
                      </div>
                      <div className="text-3xl font-bold">
                        {activeSubmissions.eventsWithActiveSubmissions || 0}
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-gold to-yellow-500 rounded-xl p-4 text-white">
                      <div className="text-sm opacity-90 mb-1">
                        Total Projected Score
                      </div>
                      <div className="text-3xl font-bold">
                        {activeSubmissions.events
                          ?.reduce(
                            (sum, evt) => sum + (evt.totalProjectedScore || 0),
                            0,
                          )
                          .toFixed(1) || 0}
                      </div>
                    </div>
                  </div>

                  {/* Events Table */}
                  {activeSubmissions.events &&
                  activeSubmissions.events.length > 0 ? (
                    <>
                      {/* Desktop Table */}
                      <div className="hidden md:block overflow-x-auto bg-white border border-gray-100 rounded-xl">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr className="text-left text-medium-grey">
                              <th className="py-3 px-4 font-medium">Event</th>
                              <th className="py-3 px-4 font-medium">
                                Submissions
                              </th>
                              <th className="py-3 px-4 font-medium">Leagues</th>
                              <th className="py-3 px-4 font-medium">
                                Projected Score
                              </th>
                              <th className="py-3 px-4 font-medium">
                                Current Score
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {activeSubmissions.events.map((evt) => (
                              <tr
                                key={evt.eventId}
                                className="border-t border-gray-100 hover:bg-gray-50"
                              >
                                <td className="py-3 px-4">
                                  <div className="font-medium text-dark-grey">
                                    {evt.eventName}
                                  </div>
                                  <div className="text-xs text-medium-grey">
                                    ID: {evt.eventId}
                                  </div>
                                </td>
                                <td className="py-3 px-4">
                                  <span className="chip chip--info">
                                    {evt.submissionCount}
                                  </span>
                                </td>
                                <td className="py-3 px-4">
                                  <span className="chip chip--muted">
                                    {evt.leagueCount}
                                  </span>
                                </td>
                                <td className="py-3 px-4 font-medium text-dark-grey">
                                  {evt.totalProjectedScore?.toFixed(1) || "0.0"}
                                </td>
                                <td className="py-3 px-4 font-medium text-dark-grey">
                                  {evt.totalCurrentScore?.toFixed(1) || "0.0"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Mobile Cards */}
                      <div className="md:hidden space-y-3">
                        {activeSubmissions.events.map((evt) => (
                          <div
                            key={evt.eventId}
                            className="bg-white border border-gray-100 rounded-lg p-4"
                          >
                            <div className="font-bold text-dark-grey mb-2">
                              {evt.eventName}
                            </div>
                            <div className="text-xs text-medium-grey mb-3">
                              ID: {evt.eventId}
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div>
                                <div className="text-medium-grey text-xs mb-1">
                                  Submissions
                                </div>
                                <div className="chip chip--info">
                                  {evt.submissionCount}
                                </div>
                              </div>
                              <div>
                                <div className="text-medium-grey text-xs mb-1">
                                  Leagues
                                </div>
                                <div className="chip chip--muted">
                                  {evt.leagueCount}
                                </div>
                              </div>
                              <div>
                                <div className="text-medium-grey text-xs mb-1">
                                  Projected Score
                                </div>
                                <div className="font-medium text-dark-grey">
                                  {evt.totalProjectedScore?.toFixed(1) || "0.0"}
                                </div>
                              </div>
                              <div>
                                <div className="text-medium-grey text-xs mb-1">
                                  Current Score
                                </div>
                                <div className="font-medium text-dark-grey">
                                  {evt.totalCurrentScore?.toFixed(1) || "0.0"}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="bg-gray-50 rounded-xl p-8 text-center">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CalendarIcon
                          className="w-8 h-8 text-gray-400"
                          aria-hidden="true"
                        />
                      </div>
                      <h3 className="text-lg font-bold text-dark-grey mb-2">
                        No active submissions
                      </h3>
                      <p className="text-medium-grey">
                        Click "Get Active Submissions" to load data.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {!activeSubmissions && !submissionsLoading && (
                <div className="bg-gray-50 rounded-xl p-8 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CalendarIcon
                      className="w-8 h-8 text-gray-400"
                      aria-hidden="true"
                    />
                  </div>
                  <h3 className="text-lg font-bold text-dark-grey mb-2">
                    No data loaded
                  </h3>
                  <p className="text-medium-grey">
                    Click "Get Active Submissions" to view submission data.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab panel: Leagues (Admin only) */}
        {activeTab === "leagues" && user?.role === "admin" && (
          <div role="tabpanel" className="p-4 md:p-6">
            <div className="space-y-4">
              {/* League Management Header */}
              <div>
                <h3 className="text-lg font-bold text-dark-grey">
                  League Management
                </h3>
                <p className="text-sm text-medium-grey">
                  Recalculate league member scores and manage league data
                </p>
              </div>

              {/* Recalculate League Scores Section */}
              <div className="bg-white border border-gray-100 rounded-xl p-6">
                <div className="space-y-4">
                  <div>
                    <h4 className="text-base font-bold text-dark-grey mb-1">
                      Recalculate All League Member Scores
                    </h4>
                    <p className="text-sm text-medium-grey">
                      Force a full recalculation of all member scores across all
                      leagues
                    </p>
                  </div>

                  <div>
                    <button
                      onClick={onRecalculateLeagueScores}
                      disabled={recalculateLeagueMemberScores.isLoading}
                      className="btn btn--primary"
                    >
                      <RefreshIcon className="w-4 h-4" aria-hidden="true" />
                      {recalculateLeagueMemberScores.isLoading
                        ? "Recalculating..."
                        : "Recalculate All League Scores"}
                    </button>
                  </div>

                  {/* Info Box */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex gap-3">
                      <div className="flex-shrink-0">
                        <svg
                          className="w-5 h-5 text-blue-600"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h5 className="text-sm font-bold text-blue-900 mb-1">
                          What does this do?
                        </h5>
                        <p className="text-sm text-blue-800">
                          This will recalculate all member scores for all
                          leagues by re-processing all submissions and updating
                          league standings. Use this if scores appear incorrect
                          or after fixing scoring logic.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab panel: Users (Admin only) */}
        {activeTab === "users" && user?.role === "admin" && (
          <div role="tabpanel" className="p-4 md:p-6">
            <UserManagement />
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
